/**
 * Period filters, as in FP&A's original dashboard (fpa-backend ADR-0006): a year, Q1–Q4 and Full
 * year chips, a 12-month grid (any combination of months of that year, adjacent or not) and an
 * "as of" date inside the period. A pill says whether the view is live (green: the current quarter
 * as of today) or a point in time (amber: "As of <date>"); "Jump to live" resets period and date.
 * The panel collapses to a one-line summary ("Q4 2026 · Live"); the choice is remembered per
 * viewer. The selection itself lives in the URL (usePeriod.ts), so links are shareable.
 */
import { type ChangeEvent, useCallback, useId, useMemo, useState } from 'react'

import type { Periods, RunwayPeriod } from '@/api/types'
import { ICONS } from '@/components/icons/icons'
import {
  ALL_MONTHS,
  businessToday,
  mediumDate,
  MONTH_ABBR,
  normalizeMonths,
  quarterMonths,
  quarterOfMonth,
  QUARTERS,
  sameMonths,
} from '@/lib/period'

import { type LiveContext, usePeriodActions } from '../hooks/usePeriod'
import { filterSummary, liveText } from '../lib/periodText'
import styles from './PeriodFilters.module.css'

const STORAGE_KEY = 'fpa.filters.expanded'

function readExpanded(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'false'
  } catch {
    return true
  }
}

function writeExpanded(value: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value))
  } catch {
    // Private mode or blocked storage: the panel just opens expanded next time.
  }
}

interface MonthMeta {
  month: number
  started: boolean
  hasData: boolean
}

/** Months of `year` with whether they have started and have data. */
function monthsOf(periods: Periods | undefined, year: number, today: string): MonthMeta[] {
  const listed = periods?.years.find((y) => y.year === year)?.months
  const ty = Number(today.slice(0, 4))
  const tm = Number(today.slice(5, 7))
  return ALL_MONTHS.map((month) => {
    const row = listed?.find((m) => m.month === month)
    return {
      month,
      started: row?.started ?? (year < ty || (year === ty && month <= tm)),
      hasData: row ? row.has_actuals || row.has_budget : true,
    }
  })
}

/** A selection is valid when its first month has started (the API refuses one that has not). */
function startsStarted(months: readonly number[], meta: readonly MonthMeta[]): boolean {
  const first = months[0]
  return first !== undefined && meta.find((m) => m.month === first)?.started === true
}

function QuarterChip({
  label,
  pressed,
  disabled,
  value,
  onPick,
}: {
  label: string
  pressed: boolean
  disabled: boolean
  value: number
  onPick: (value: number) => void
}) {
  const handleClick = useCallback(() => onPick(value), [onPick, value])
  return (
    <button type="button" className={styles.chip} aria-pressed={pressed} disabled={disabled} onClick={handleClick}>
      {label}
    </button>
  )
}

function MonthCell({
  meta,
  selected,
  onToggle,
}: {
  meta: MonthMeta
  selected: boolean
  onToggle: (month: number) => void
}) {
  const handleClick = useCallback(() => onToggle(meta.month), [onToggle, meta.month])
  const label = MONTH_ABBR[meta.month - 1] ?? ''
  return (
    <button
      type="button"
      className={`${styles.month} ${meta.hasData ? '' : styles.noData}`}
      aria-pressed={selected}
      disabled={!meta.started}
      title={meta.started ? (meta.hasData ? undefined : 'No data in this month') : 'Not started yet'}
      onClick={handleClick}
    >
      {label}
    </button>
  )
}

export function PeriodFilters({ period, periods }: { period: RunwayPeriod; periods: Periods | undefined }) {
  const { setPeriod, setAsOf, jumpToLive } = usePeriodActions()
  const [expanded, setExpanded] = useState(readExpanded)
  const bodyId = useId()
  const yearId = useId()
  const asOfId = useId()
  const today = periods?.today ?? businessToday()
  const live = useMemo<LiveContext>(() => ({ today }), [today])
  const { year, months } = period

  const years = useMemo(() => {
    const listed = periods?.years.map((y) => y.year) ?? []
    return [...new Set([...listed, year])].sort((a, b) => b - a)
  }, [periods, year])
  const meta = useMemo(() => monthsOf(periods, year, today), [periods, year, today])
  const selected = useMemo(() => new Set(months), [months])

  const toggle = useCallback(() => {
    setExpanded((value) => {
      writeExpanded(!value)
      return !value
    })
  }, [])

  const handleYear = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const nextYear = Number(event.target.value)
      const nextMeta = monthsOf(periods, nextYear, today)
      // Keep the months when they exist in that year; else its current quarter (or Q1).
      const keep = startsStarted(months, nextMeta)
      const ty = Number(today.slice(0, 4))
      const fallback = nextYear === ty ? quarterMonths(quarterOfMonth(Number(today.slice(5, 7)))) : quarterMonths(1)
      setPeriod(nextYear, keep ? months : fallback, live)
    },
    [periods, today, months, setPeriod, live],
  )

  const pickQuarter = useCallback((q: number) => setPeriod(year, quarterMonths(q), live), [setPeriod, year, live])
  const pickFullYear = useCallback(() => setPeriod(year, ALL_MONTHS, live), [setPeriod, year, live])

  const toggleMonth = useCallback(
    (month: number) => {
      const next = selected.has(month) ? months.filter((m) => m !== month) : normalizeMonths([...months, month])
      // Never empty, and never a selection that has not started yet.
      if (next.length === 0 || !startsStarted(next, meta)) return
      setPeriod(year, next, live)
    },
    [selected, months, meta, setPeriod, year, live],
  )

  const handleAsOf = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      if (!value || value < period.start) return
      setAsOf(value >= period.as_of_max ? null : value, { year, months })
    },
    [period.start, period.as_of_max, setAsOf, year, months],
  )

  const fullYear = months.length === 12
  const pillClass = period.is_live ? styles.pillLive : styles.pillAsOf

  return (
    <section className={styles.panel} aria-label="Period filters">
      <div className={styles.head}>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={toggle}
        >
          <ICONS.calendar size={14} className={styles.icon} />
          <span className={styles.toggleLabel}>Filters</span>
          {expanded ? <ICONS.chevronUp size={14} /> : <ICONS.chevronDown size={14} />}
        </button>
        <span className={styles.summary}>{expanded ? period.label : filterSummary(period)}</span>
        <span className={`${styles.pill} ${pillClass}`} role="status">
          <span className={styles.dot} aria-hidden="true" />
          {liveText(period)}
        </span>
        {period.is_live ? null : (
          <button type="button" className={styles.link} onClick={jumpToLive}>
            Jump to live
          </button>
        )}
      </div>

      {expanded ? (
        <div className={styles.body} id={bodyId}>
          <div className={styles.row}>
            <label className={styles.label} htmlFor={yearId}>
              Year
            </label>
            <select id={yearId} className={styles.select} value={year} onChange={handleYear}>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <div className={styles.chips} role="group" aria-label="Quarters">
              {QUARTERS.map((q) => (
                <QuarterChip
                  key={q}
                  label={`Q${q}`}
                  value={q}
                  pressed={sameMonths(months, quarterMonths(q))}
                  disabled={!startsStarted(quarterMonths(q), meta)}
                  onPick={pickQuarter}
                />
              ))}
              <button
                type="button"
                className={styles.chip}
                aria-pressed={fullYear}
                disabled={!startsStarted(ALL_MONTHS, meta)}
                onClick={pickFullYear}
              >
                Full year
              </button>
            </div>
          </div>
          <div className={styles.months} role="group" aria-label={`Months of ${year}`}>
            {meta.map((m) => (
              <MonthCell key={m.month} meta={m} selected={selected.has(m.month)} onToggle={toggleMonth} />
            ))}
          </div>
          <div className={styles.row}>
            <label className={styles.label} htmlFor={asOfId}>
              View as of
            </label>
            <input
              id={asOfId}
              className={styles.date}
              type="date"
              min={period.start}
              max={period.as_of_max}
              value={period.as_of}
              onChange={handleAsOf}
            />
            <span className={styles.hint}>
              {mediumDate(period.start)} – {mediumDate(period.as_of_max)}
            </span>
          </div>
        </div>
      ) : null}
    </section>
  )
}
